
<script setup>
/*
	Component for displaying basic data in a table format.
	Data: Array of objects with the data for each row.
	Columns: Array of objects that defines the columns and their headings.
						Order in object is order displayed. If not defined, objects shown with keys
						as columns and all data is shown.
 */


import { computed } from 'vue'
import { Format } from '@/models/utils/FormatUtils.js'

const props = defineProps({
	data: {
		type: Object,
		required: true
	},
	/**
	 * Set keys if there are values to ignore
	 * Array of { "key": string, "title": string, "width": string,
	 *            "type": "date", "date_format": Object }
	 * "type" and "date_format" are optional; "type": "date" forces date formatting for the
	 * column instead of relying on value-shape detection. "date_format" is an
	 * Intl.DateTimeFormat options object passed straight to Format.date.
	 */
	columns: {
		type: Array,
		default: () => []
	},
	modal:{
		type: Boolean,
		default: false
	}
})

/**
 * Gets the list of keys, either given or from the object.
 * @type {ComputedRef<[]|*|string[]>}
 */
const displayColumns = computed(() => {
	if (!props.data) return [];
	if (props.columns.length > 0 && props.data.length > 0) {
		return props.columns.filter(column => column.key in props.data[0])
	}
	if (props.data.length > 0) {
		return Object.keys(props.data[0])
	}
	return []
})

/**
 * Format the value so it easier to read
 * @param {*} p_value Raw cell value
 * @param {{"title": string, "key": string, "width": string, "type": string, "date_format": string}} p_column Column definition; `type: 'date'` forces date formatting
 * @returns {string}
 */
function formatValue(p_value, p_column)
{
	if (p_value === null) return 'null';
	if (p_value === undefined) return 'undefined';
	if (typeof p_value === 'number') return String(p_value);
	if (typeof p_value === 'boolean') return String(p_value);

	//An explicit column type wins over shape detection
	if (p_column?.type === 'date') return Format.date(p_value, p_column.date_format);

	if (p_value instanceof Date) return Format.date(p_value);

	//This will check the text for a date format and return a string if it doesn't match
	if (typeof p_value === 'string') return Format.formatDateString(p_value);

	if (typeof p_value === 'object') return JSON.stringify(p_value, null, 2);
	return String(p_value);
}

</script>

<template>
	<div :class="{'overflow-y-scroll': props.modal}">

		<table v-if="data != null" class="  min-w-30" :class="{'w-full': modal}">
			<thead>

			<!-- Column header -->
			<tr class="">
				<th v-for="column in displayColumns" :key="column" class="sticky top-0 z-1 bg-primary-lightest font-bold value text-sm px-1 py-1 min-w-30" :style="{ width: column.width }">
					{{ column.title }}
				</th>
			</tr>
			</thead>
			<tbody>

			<!-- Values -->
			<tr v-for="record in props.data" :key="record" class="even:bg-primary-subtle" >
				<td v-for="column in displayColumns" :key="column" class="value text-sm px-1 py-1 min-w-30">
					<div class="text-sm !px-1 !py-0 min-w-30">
						{{ formatValue(record[column.key], column) }}
					</div>
				</td>
			</tr>

			</tbody>
		</table>
	</div>

</template>

<style scoped>
.object-viewer .key {
	background: #f5f5f5;
	@apply text-sm w-px !px-1 !py-0 align-top font-bold;/* w-px - forces key column to fit content *//* min-w-30 break-all add to make key smaller */
	@apply !border !border-solid !border-neutral-subtle !border-opacity-50;
}
.value {
	border: 1px solid #ddd;
}
</style>
